import { PageShell, DemoCTA } from "../components/PageShell";
import { CayusWave } from "../components/CayusWave";
import HumanQuoteSection from "../components/HumanQuoteSection";
import { useLanguage } from "../context/LanguageContext";
import { usePages } from "../translations/pages";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const LEVEL_COLORS = ["#DBEAFE", "#93C5FD", "#0047AB", "#1E3A8A"];

const Eyebrow = ({ children }) => <p className="mono-cayus text-[#0047AB] mb-4">{children}</p>;
const H2 = ({ children }) => <h2 className="font-bold text-3xl text-[#1B1B1B] tracking-[-0.025em] mb-10">{children}</h2>;

export default function MethodologiePage() {
  const { language } = useLanguage();
  const p = usePages(language).methodo;
  const { journey: JOURNEY, ladders: LADDERS, levels: LEVELS, principes: PRINCIPES, glossaire: GLOSSAIRE } = p;
  return (
    <PageShell testid="methodologie-page">
      {/* Hero */}
      <div className="wrap-cayus mb-14">
        <p className="mono-cayus text-[#0047AB] mb-4">{p.eyebrow}</p>
        <h1 className="font-extrabold text-4xl lg:text-5xl text-[#1B1B1B] tracking-[-0.03em] mb-5">
          {p.title}
        </h1>
        <p className="font-body-cayus text-lg text-[#4D4D4D] max-w-2xl mx-auto">
          {p.lead}
        </p>
      </div>
      <CayusWave className="w-full h-8 mb-14" opacity={0.3} />

      {/* Le voyage d'un signal */}
      <section className="wrap-cayus mb-20" data-testid="signal-journey">
        <Eyebrow>{p.journeyEyebrow}</Eyebrow>
        <H2>{p.journeyTitle}</H2>
        <div className="space-y-0">
          {JOURNEY.map((s, i) => (
            <div key={i} className={`flex gap-5 ${s.wall ? "" : ""}`}>
              <div className="flex flex-col items-center">
                <span className={`w-9 h-9 rounded-full flex items-center justify-center mono-cayus text-[11px] flex-shrink-0 ${s.wall ? "bg-[#0A2342] text-white" : "bg-[#0047AB]/10 text-[#0047AB]"}`}>
                  {i + 1}
                </span>
                {i < JOURNEY.length - 1 && <span className={`w-px flex-1 ${s.wall ? "bg-[#0A2342]" : "bg-[#C3CFE0]"}`} style={s.wall ? { width: 4 } : {}} />}
              </div>
              <div className={`pb-8 ${s.wall ? "bg-[#0A2342] text-white rounded-xl px-5 py-4 mb-4 flex-1 pb-4" : "flex-1"}`}>
                <h3 className={`font-display font-semibold text-lg mb-1 ${s.wall ? "text-white" : "text-[#0A2342]"}`}>{s.t}</h3>
                <p className={`font-body-cayus text-sm leading-relaxed mb-2 ${s.wall ? "text-[#E4EEFC]" : "text-[#4D4D4D]"}`}>{s.d}</p>
                <p className={`mono-cayus text-[10px] ${s.wall ? "text-[#9ECEFA]" : "text-[#9098A3]"}`}>{p.whoSees} : {s.who}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Les six Behaviour Ladders */}
      <section className="bg-[#F7F9FC] py-16 mb-20" data-testid="ladders-section">
        <div className="wrap-cayus">
          <Eyebrow>{p.laddersEyebrow}</Eyebrow>
          <H2>{p.laddersTitle}</H2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {LADDERS.map((l, i) => (
              <div key={i} className="bg-white border border-[#C3CFE0] rounded-xl p-6">
                <h3 className="font-display font-semibold text-lg text-[#0A2342] mb-1.5">{l.n}</h3>
                <p className="font-body-cayus text-sm text-[#4D4D4D] mb-4">{l.d}</p>
                <p className="font-body-cayus text-sm text-[#0047AB] italic border-l-2 border-[#0047AB]/30 pl-3">« {l.ex} »</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* L'échelle */}
      <section className="wrap-cayus mb-20" data-testid="staircase-section">
        <Eyebrow>{p.levelsEyebrow}</Eyebrow>
        <H2>{p.levelsTitle}</H2>
        <div className="flex items-end gap-3 mb-8">
          {LEVELS.map((l, i) => (
            <div key={i} className="flex-1 text-center group">
              <div className="rounded-t-xl transition-transform group-hover:-translate-y-1" style={{ height: 48 + i * 28, backgroundColor: LEVEL_COLORS[i] }} />
              <p className="mono-cayus text-[10px] sm:text-[11px] text-[#0A2342] mt-2 font-semibold">{l.n}</p>
              <p className="font-body-cayus text-xs text-[#5E6C83] mt-1 leading-snug hidden sm:block">{l.d}</p>
            </div>
          ))}
        </div>
        <div className="border-l-[3px] border-[#F59E0B] bg-[#F59E0B]/5 rounded-r-xl px-5 py-4">
          <p className="mono-cayus text-[10px] text-[#F59E0B] mb-1">Building Evidence</p>
          <p className="font-body-cayus text-sm text-[#3A3F4B]">
            « {p.buildingQuote} »
          </p>
        </div>
      </section>

      {/* Module Changement */}
      <section className="wrap-cayus mb-20" data-testid="change-module-section">
        <Eyebrow>{p.changeEyebrow}</Eyebrow>
        <H2>{p.changeTitle}</H2>
        <div className="grid sm:grid-cols-2 gap-5 mb-5">
          {["Making sense of change", "Working with uncertainty"].map((n, i) => (
            <div key={i} className="bg-white border border-[#C3CFE0] rounded-xl p-6 text-center">
              <h3 className="font-display font-semibold text-lg text-[#0A2342]">{n}</h3>
            </div>
          ))}
        </div>
        <p className="font-body-cayus text-sm text-[#4D4D4D] text-center max-w-2xl mx-auto">
          {p.changeText}
        </p>
      </section>

      {/* Les principes */}
      <section className="bg-[#F7F9FC] py-16 mb-20" data-testid="principles-section">
        <div className="wrap-cayus">
          <Eyebrow>{p.principesEyebrow}</Eyebrow>
          <H2>{p.principesTitle}</H2>
          <ul className="grid sm:grid-cols-2 gap-4 mb-8">
            {PRINCIPES.map((p, i) => (
              <li key={i} className="bg-white border border-[#C3CFE0] rounded-xl px-5 py-4 font-body-cayus text-sm text-[#3A3F4B]">{p}</li>
            ))}
          </ul>
          <div className="border border-[#C3CFE0] rounded-xl overflow-hidden grid sm:grid-cols-2">
            <div className="p-6 bg-white">
              <p className="mono-cayus text-[10px] text-[#9098A3] mb-2">{p.survey}</p>
              <p className="font-body-cayus text-sm text-[#4D4D4D]">{p.surveyText}</p>
            </div>
            <div className="p-6 ink-section">
              <p className="mono-cayus text-[10px] text-[#9ECEFA] mb-2">{p.continuous}</p>
              <p className="font-body-cayus text-sm text-[#E4EEFC]">{p.continuousText}</p>
            </div>
          </div>
        </div>
      </section>

      {/* La science */}
      <section className="wrap-cayus mb-20" data-testid="science-section">
        <Eyebrow>{p.scienceEyebrow}</Eyebrow>
        <H2>{p.scienceTitle}</H2>
        <div className="grid sm:grid-cols-2 gap-6">
          {p.bios.map((b, i) => (
            <div key={i} className="border border-[#C3CFE0] rounded-xl p-7">
              <h3 className="font-display font-semibold text-xl text-[#0A2342] mb-1">{b.name}</h3>
              <p className="mono-cayus text-[10px] text-[#0047AB] mb-3">{b.role}</p>
              <p className="font-body-cayus text-sm text-[#4D4D4D] leading-relaxed">{b.bio}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Glossaire */}
      <section className="wrap-cayus mb-20" data-testid="glossary-section">
        <Eyebrow>{p.glossEyebrow}</Eyebrow>
        <H2>{p.glossTitle}</H2>
        <Accordion type="single" collapsible className="w-full">
          {GLOSSAIRE.map(([term, def], i) => (
            <AccordionItem key={i} value={`g-${i}`}>
              <AccordionTrigger className="font-display font-semibold text-[#0A2342]">{term}</AccordionTrigger>
              <AccordionContent className="font-body-cayus text-[#4D4D4D]">{def}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <HumanQuoteSection />

      <DemoCTA />
    </PageShell>
  );
}
